import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TestStepService } from '../../../services/test-step.service';
import { TestModuleStore } from '../../../services/test-module-store';
import { TestModule } from '../../../models/test-module.model';
import { TestStep } from '../../../models/test-case.model';
import { Testdatenblatt } from '../../../models/testdatenblatt.model';
import { activeRowCount } from '../../../shared/data-sheet-filters.codec';
import { StepKeyPillComponent } from '../../../components/step-key-pill/step-key-pill.component';

/**
 * Rekursive, read-only aufklappbare Anzeige eines eingebundenen Testmoduls (NEX-686). Zeigt
 * beim Aufklappen die Schritte des Moduls und — für verschachtelt eingebundene Module —
 * denselben Baum eine Ebene tiefer.
 *
 * <p>Rekursive Aufrufe sind erlaubt (NEX-2257), werden aber nicht endlos aufgeklappt:
 * {@link ancestors} trägt den Pfad der bereits geöffneten Module. Liegt dieses Modul selbst
 * schon darauf, zeigt der Knoten statt Kopf und Schritten einen neutralen Hinweis (NEX-2259)
 * und lädt nichts. Zusätzlich begrenzt {@link MAX_DEPTH} die Tiefe als Backstop.
 */
@Component({
  selector: 'app-module-step-tree',
  imports: [TranslatePipe, MatIcon, MatProgressSpinner, ModuleStepTreeComponent, StepKeyPillComponent],
  templateUrl: './module-step-tree.component.html',
  styleUrl: './module-step-tree.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModuleStepTreeComponent implements OnInit {
  private readonly stepService = inject(TestStepService);
  private readonly moduleStore = inject(TestModuleStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);

  static readonly MAX_DEPTH = 8;

  readonly projectId = input.required<string>();
  readonly moduleId = input.required<string>();
  readonly name = input('');
  /**
   * Projektweite Testdatenblatt-Liste. Wird durchgereicht vom Testfall-/Modul-Editor,
   * damit die verschachtelte Modul-Kopfzeile pro verbundenem TDB einen Chip zeigt.
   */
  readonly availableSheets = input<readonly Testdatenblatt[]>([]);
  /** Modul-IDs auf dem Pfad von der Wurzel bis zum Elternmodul (für die Rekursions-Erkennung). */
  readonly ancestors = input<readonly string[]>([]);
  /**
   * Durchgängige hierarchische Nummer des Elternteils (z. B. "3" oder "3.2"), NEX-826.
   * `null` an der Wurzel — die Kind-Nummern lauten dann `1, 2, 3, …`, sonst `{prefix}.1, …`.
   */
  readonly prefix = input<string | null>(null);

  /** NEX-1296: Schritt-Nummern als Pillen zeigen (nur im Testfall-Editor). */
  readonly showStepKeys = input(false);
  /** NEX-1296: Nummer der MODULE_REF-Zeile, die dieses Modul einbindet; Pille im Kopf. */
  readonly stepKey = input<string | null | undefined>(null);
  readonly readOnly = input(false);
  readonly canEditModule = input(true);

  /** Bearbeiten eines (auch tief verschachtelten) Moduls im Drawer — blubbert bis zur Tabelle. */
  readonly editModule = output<string>();

  readonly expanded = signal(false);
  readonly loading = signal(false);
  readonly error = signal(false);
  private readonly loadedSteps = signal<TestStep[]>([]);
  private loadedOnce = false;
  private readonly loaded = signal(false);

  /** Live-Titel aus dem Store (nach Drawer-Save), sonst der übergebene Name. */
  readonly liveName = computed(() => this.moduleStore.watch(this.moduleId())()?.name ?? this.name());

  private readonly storeSteps = computed<TestStep[] | undefined>(
    () => (this.moduleStore.watch(this.moduleId())() as (TestModule & { steps?: TestStep[] }) | null)?.steps,
  );

  /**
   * Anzuzeigende Schritte: nach einem Drawer-Save bevorzugt der frische Stand aus dem Store
   * (damit Änderungen an einem verschachtelten Modul sofort in der Mitte erscheinen), sonst
   * die lazy geladenen Schritte.
   */
  readonly steps = computed<TestStep[]>(() => {
    const live = this.storeSteps();
    if (live) {
      return [...live].sort((a, b) => a.orderIndex - b.orderIndex);
    }
    return this.loadedSteps();
  });

  /** Pfad inkl. dieses Moduls — wird an die Kinder weitergegeben. */
  readonly childAncestors = computed(() => [...this.ancestors(), this.moduleId()]);

  /** Dieses Modul liegt schon auf dem Pfad: Hinweis statt Aufklappen (NEX-2259). */
  readonly isRecursion = computed(() => this.ancestors().includes(this.moduleId()));

  readonly recursionKey = computed(() => {
    if (this.ancestors().at(-1) === this.moduleId()) return 'testCases.form.testSteps.moduleRecursionSelf';
    return this.liveName().trim()
      ? 'testCases.form.testSteps.moduleRecursion'
      : 'testCases.form.testSteps.moduleRecursionUnnamed';
  });

  readonly stepCountKnown = computed(() => this.loaded() || !!this.storeSteps());

  /** Anzahl direkter Einzelschritte (kein MODULE_REF) — Chip in der Kopfzeile. */
  readonly stepCount = computed(() => this.steps().filter((s) => !this.isModuleRef(s)).length);

  /** Anzahl direkt eingebundener Untermodule — Suffix in der Kopfzeile. */
  readonly submoduleCount = computed(() => this.steps().filter((s) => this.isModuleRef(s)).length);

  /** Testdatenblaetter, die mit diesem verschachtelten Modul verbunden sind. */
  readonly linkedSheets = computed<Testdatenblatt[]>(() => {
    const id = this.moduleId();
    return this.availableSheets().filter((s) => s.testModuleIds?.includes(id));
  });

  /** Chip-Text pro TDB: „TDB-05 · 2×". */
  chipLabel(sheet: Testdatenblatt): string {
    return this.translate.instant('tdb.chip.compact', {
      key: sheet.key,
      count: activeRowCount(sheet.zeilen.length, sheet.zeilenAktiv),
    });
  }

  chipTooltip(sheet: Testdatenblatt): string {
    return this.translate.instant('tdb.chip.tooltipLinked', { key: sheet.key, count: sheet.zeilen.length });
  }

  /** Zeile im aufgeklappten Bereich: „TDB-05 (2 Zeilen)" in Mono. */
  detailLabel(sheet: Testdatenblatt): string {
    return this.translate.instant('tdb.chip.detail', { key: sheet.key, count: sheet.zeilen.length });
  }

  protected readonly maxDepth = ModuleStepTreeComponent.MAX_DEPTH;

  isModuleRef(step: TestStep): boolean {
    return step.kind === 'MODULE_REF';
  }

  /**
   * Durchgängige hierarchische Nummer des Kindes an `index` (NEX-826). Als String, damit
   * `1.1` nicht als Float geparst wird.
   */
  numberFor(index: number): string {
    const parentPrefix = this.prefix();
    return parentPrefix ? `${parentPrefix}.${index + 1}` : `${index + 1}`;
  }

  ngOnInit(): void {
    if (!this.isRecursion()) {
      this.load();
    }
  }

  toggle(): void {
    const willExpand = !this.expanded();
    this.expanded.set(willExpand);
    if (willExpand && !this.loadedOnce) {
      this.load();
    }
  }

  onHeaderKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggle();
    }
  }

  onEditClick(event: Event): void {
    event.stopPropagation();
    if (this.canEditModule()) {
      this.editModule.emit(this.moduleId());
    }
  }

  onEditKeydown(event: KeyboardEvent): void {
    // Sonst klappt der Kopf auf Enter/Leertaste und verhindert dabei den Klick des Buttons.
    if (event.key === 'Enter' || event.key === ' ') {
      event.stopPropagation();
    }
  }

  onRecursionOpen(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.editModule.emit(this.moduleId());
  }

  retry(): void {
    this.load();
  }

  private load(): void {
    this.loadedOnce = true;
    this.loading.set(true);
    this.error.set(false);
    this.stepService
      .listForModule(this.projectId(), this.moduleId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (steps) => {
          this.loadedSteps.set([...steps].sort((a, b) => a.orderIndex - b.orderIndex));
          this.loaded.set(true);
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
          this.error.set(true);
          this.loadedOnce = false;
        },
      });
  }
}
